/** Einheitliche Formatierung aller Zahlen und Zeitangaben in deutscher Schreibweise. */

const euro = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR', maximumFractionDigits: 2 });
const euroCompact = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR', notation: 'compact', maximumFractionDigits: 1 });
const number = new Intl.NumberFormat('de-DE');
const decimal = new Intl.NumberFormat('de-DE', { maximumFractionDigits: 2 });
const percent = new Intl.NumberFormat('de-DE', { style: 'percent', maximumFractionDigits: 1 });

export function money(value: number, compact = false): string {
  if (!Number.isFinite(value)) return '–';
  return compact && Math.abs(value) >= 100_000 ? euroCompact.format(value) : euro.format(value);
}

export function num(value: number): string {
  return Number.isFinite(value) ? number.format(Math.round(value)) : '–';
}

export function dec(value: number, digits = 2): string {
  if (!Number.isFinite(value)) return '–';
  return new Intl.NumberFormat('de-DE', { maximumFractionDigits: digits, minimumFractionDigits: digits }).format(value);
}

export function pct(fraction: number, digits = 1): string {
  if (!Number.isFinite(fraction)) return '–';
  return new Intl.NumberFormat('de-DE', { style: 'percent', maximumFractionDigits: digits }).format(fraction);
}

/** Prozentwert, der bereits als 0..100 vorliegt. */
export function pct100(value: number, digits = 1): string {
  return `${dec(value, digits)} %`;
}

export function mbps(value: number): string {
  if (!Number.isFinite(value)) return '–';
  if (value >= 1_000_000) return `${decimal.format(value / 1_000_000)} Tbit/s`;
  if (value >= 1000) return `${decimal.format(value / 1000)} Gbit/s`;
  return `${decimal.format(value)} Mbit/s`;
}

export function bytes(gb: number): string {
  if (!Number.isFinite(gb)) return '–';
  if (gb >= 1_048_576) return `${decimal.format(gb / 1_048_576)} PB`;
  if (gb >= 1024) return `${decimal.format(gb / 1024)} TB`;
  return `${number.format(Math.round(gb))} GB`;
}

export function watt(w: number): string {
  return w >= 1000 ? `${decimal.format(w / 1000)} kW` : `${number.format(Math.round(w))} W`;
}

/** Spieldatum aus dem ISO-String der Welt. */
export function gameDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

export function gameDay(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch {
    return iso;
  }
}

/** Ticks (Stunden) als lesbare Dauer. */
export function duration(ticks: number): string {
  if (!Number.isFinite(ticks) || ticks < 0) return '–';
  if (ticks < 1) return '< 1 h';
  if (ticks < 24) return `${Math.round(ticks)} h`;
  const days = Math.floor(ticks / 24);
  const hours = Math.round(ticks % 24);
  if (days < 30) return hours > 0 ? `${days} d ${hours} h` : `${days} d`;
  const months = Math.floor(days / 30);
  return `${months} Mon. ${days % 30} d`;
}

/** Relative Angabe zum aktuellen Tick ("vor 3 h"). */
export function sinceTick(tick: number, currentTick: number): string {
  const delta = currentTick - tick;
  if (delta <= 0) return 'gerade eben';
  return `vor ${duration(delta)}`;
}

const relative = new Intl.RelativeTimeFormat('de-DE', { numeric: 'auto' });

/**
 * Echte Uhrzeit relativ zum Jetzt ("vor 3 Stunden").
 *
 * Nicht zu verwechseln mit `sinceTick`: Das rechnet in Spielstunden. Hier geht
 * es um Zeitstempel des Servers – wann zuletzt gespeichert wurde, nicht wann im
 * Spiel etwas geschah.
 */
export function sinceDate(iso: string, now: number = Date.now()): string {
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return '–';

  // Von der kleinsten Einheit aufwärts, bis der Wert in die Einheit passt. Der
  // zweite Wert ist jeweils der Umrechnungsfaktor zur nächstgrößeren.
  const steps: [Intl.RelativeTimeFormatUnit, number][] = [
    ['second', 60],
    ['minute', 60],
    ['hour', 24],
    ['day', 7],
    ['week', 4.35],
    ['month', 12],
  ];
  let value = (then - now) / 1000;
  for (const [unit, size] of steps) {
    if (Math.abs(value) < size) return relative.format(Math.round(value), unit);
    value /= size;
  }
  return relative.format(Math.round(value), 'year');
}

/** Kürzt eine ID für die Anzeige. */
export function shortId(id: string): string {
  const parts = id.split('_');
  return parts.length > 1 ? parts[1].slice(0, 6) : id.slice(0, 8);
}

export { percent, number as numberFormat };

/**
 * Ländernamen zu den Kürzeln aus dem Standortkatalog.
 *
 * Die Kürzel stehen im Katalog, weil sie dort kurz und stabil sind; angezeigt
 * werden soll aber der ausgeschriebene Name.
 */
export const COUNTRY_NAMES: Record<string, string> = {
  AT: 'Österreich',
  BE: 'Belgien',
  CH: 'Schweiz',
  CZ: 'Tschechien',
  DE: 'Deutschland',
  DK: 'Dänemark',
  ES: 'Spanien',
  FI: 'Finnland',
  FR: 'Frankreich',
  GB: 'Vereinigtes Königreich',
  IE: 'Irland',
  IT: 'Italien',
  LU: 'Luxemburg',
  NL: 'Niederlande',
  NO: 'Norwegen',
  PL: 'Polen',
  SE: 'Schweden',
  US: 'Vereinigte Staaten',
};
